import {
  Activity,
  Ban,
  CheckCircle2,
  Info,
  KeyRound,
  RotateCcw,
  ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { PermissionPicker } from '../../../../components/PermissionPicker';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileIdentity } from '../../../../components/profile/ProfileIdentity';
import { ProfileNav, type ProfileNavEntry } from '../../../../components/profile/ProfileNav';
import { findNavLabel } from '../../../../components/profile/profileSectionTitle';
import { ProfileShell } from '../../../../components/profile/ProfileShell';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { StatusText } from '../../../../components/table/StatusText';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { useAuth, usePermission } from '../../../../auth/AuthContext';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { accessLevelsApi } from '../../access-levels/accessLevelsApi';
import type { UserAccessLevel } from '../../access-levels/accessLevelTypes';
import { EditUserAccessLevelsModal } from '../../access-levels/components/EditUserAccessLevelsModal';
import { usePermissionCatalog } from '../../roles/usePermissionCatalog';
import { EditUserInfoModal } from '../components/EditUserInfoModal';
import { EditUserRoleModal } from '../components/EditUserRoleModal';
import { PinRequirementDialog } from '../components/PinRequirementDialog';
import { ResetUserPasswordModal } from '../components/ResetUserPasswordModal';
import { ResetUserPinModal } from '../components/ResetUserPinModal';
import { UserConfirmDialog, type UserConfirmKind } from '../components/UserConfirmDialog';
import { useUserProfile } from '../useUserProfile';
import { usersApi } from '../usersApi';
import type { LoginAttempt, UserDetail } from '../userTypes';

type PanelKey = 'info' | 'access' | 'credentials' | 'sign-ins';

/** Which card's editor is open. One card, one modal. */
type EditTarget = 'info' | 'roles' | 'password' | 'levels' | 'pin-requirement' | 'pin';

/**
 * The user profile page, on the sidebar profile layout.
 *
 * `Info` is the first panel on every profile: what the record is, plus the audit trail of
 * who created and last changed it. Each group of facts is a `<ProfileCard>`, and a card
 * whose facts can be changed carries an Edit button that opens a modal for that card alone.
 * The audit card has none — a record of what happened is never editable.
 */
export function UserProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();

  const canReadSignIns = usePermission('users.impersonate-audit');
  // The role link points at a page gated on this key; without it the link would bounce.
  const canReadRoles = usePermission('roles.read');
  const { user, loading, error, reload } = useUserProfile(id);
  // Sign-in history is a standard table: searched, sorted and paged on the server.
  const fetchSignIns = useCallback(
    (query: DataTableQuery, signal: AbortSignal) => usersApi.loginActivity(id ?? '', query, signal),
    [id],
  );

  const activity = useDataTable<LoginAttempt>({
    fetchPage: fetchSignIns,
    enabled: Boolean(id) && canReadSignIns,
  });

  const { catalog, loading: catalogLoading } = usePermissionCatalog();

  const [panel, setPanel] = useState<PanelKey>('info');
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [confirming, setConfirming] = useState<UserConfirmKind | null>(null);

  /*
    THE ACCOUNT'S CLEARANCES, one row per level-gated module, tagged or not.

    Its own fetch rather than a field on the user DTO: the list is derived from the module
    registry, not from the account's own row, and an account with no tags still has a row per
    gated module. A failed fetch leaves the card empty rather than taking the page down — it is
    one card on one panel, and the rest of the profile is unaffected.
  */
  const [userLevels, setUserLevels] = useState<UserAccessLevel[]>([]);

  const loadLevels = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      accessLevelsApi
        .userLevels(id, signal)
        .then((next) => {
          if (!signal?.aborted) setUserLevels(next);
        })
        .catch(() => undefined);
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadLevels(controller.signal);

    return () => controller.abort();
  }, [loadLevels]);

  const entries: ProfileNavEntry[] = [
    { key: 'info', label: 'Info', icon: <Info className="h-4 w-4" /> },
    {
      key: 'credentials',
      label: 'Credentials',
      icon: <KeyRound className="h-4 w-4" />,
      permission: 'users.reset-password',
    },
    {
      key: 'access',
      label: 'Roles and Permissions',
      icon: <ShieldCheck className="h-4 w-4" />,
      count: user?.permissionKeys.length ?? 0,
    },
    {
      key: 'sign-ins',
      label: 'Sign-in activity',
      icon: <Activity className="h-4 w-4" />,
      count: activity.total,
      permission: 'users.impersonate-audit',
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Administration',
          { label: 'Users', to: '/users' },
          user?.displayName ?? 'Account',
        ]}
      />

      {!id ? (
        <ErrorState message="That is not a valid account reference." />
      ) : loading ? (
        <ProfileSkeleton />
      ) : error ? (
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          // A missing account will still be missing on a retry; anything else may not be.
          onRetry={error.isNotFound ? undefined : reload}
        />
      ) : user ? (
        <ProfileShell
          /* The heading every panel below states. Resolved from the nav rather than
             typed, so the rail and the heading cannot name the section differently. */
          sectionTitle={findNavLabel(entries, panel)}
          id="user-profile"
          banner={<LifecycleBanner user={user} onAct={setConfirming} />}
          sidebar={
            <>
              <ProfileIdentity
                id="user-profile-identity"
                name={user.displayName}
                email={user.email}
                title={user.roleName ?? 'No role assigned'}
                status={
                  <StatusText tone={user.isActive ? 'active' : 'inactive'}>
                    {user.isActive ? 'active' : 'inactive'}
                  </StatusText>
                }
              />

              <ProfileNav
                id="user-profile-nav"
                entries={entries}
                activeKey={panel}
                onSelect={(key) => setPanel(key as PanelKey)}
                hasPermission={hasPermission}
              />
            </>
          }
        >
          {panel === 'info' ? (
            <>
              <ProfileCard
                id="user-profile-basic-info"
                title="Basic Info"
                onEdit={() => setEditing('info')}
                editPermission="users.update"
              >
                <PreviewTable variant="table-display" rows={basicInfoRows(user)} />
              </ProfileCard>

              {/*
                No Status card: the banner above already states whether the account is active
                and carries the button that changes it. A card repeating it would put the same
                value in two slots, and the sidebar pill is the always-on indicator.
              */}

              {/* Never editable: the system writes these and they record what happened. */}
              <ProfileCard id="user-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(user)} />
              </ProfileCard>
            </>
          ) : null}

          {panel === 'access' ? (
            <>
              <ProfileCard
                id="user-profile-roles"
                title="Role"
                onEdit={() => setEditing('roles')}
                editPermission="users.update"
              >
                <PreviewTable
                  variant="table-display"
                  rows={[
                    {
                      label: 'Assigned',
                      /*
                        A link to the role's own page, which is where "what does this actually
                        grant" is answered. A `<Link>` rather than a button: it navigates, so
                        the browser's back works and the row can be opened in a new tab.

                        Gated on `roles.read`, the key that page is gated on — an account
                        without it would follow the link into a permission bounce. Ungated it
                        falls back to the plain name: the fact is still worth stating, it just
                        stops being a way to get anywhere.
                      */
                      value:
                        user.roleId && canReadRoles ? (
                          <Link
                            id="user-profile-role-link"
                            to={`/roles/${user.roleId}`}
                            className="rounded font-medium text-slate-800 underline-offset-2 transition hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
                          >
                            {user.roleName}
                          </Link>
                        ) : (
                          user.roleName
                        ),
                    },
                  ]}
                />
              </ProfileCard>

              {/*
                THE SECOND AXIS. The role above says what this account may DO; this says which
                RECORDS it may do it to, and no arrangement of permission keys expresses that.

                Its own card and its own Edit, because the write is its own key —
                `users.assign-levels`, which is the only key in the product that widens what a
                reader can see without granting a single permission.
              */}
              <ProfileCard
                id="user-profile-access-levels"
                title="Access levels"
                onEdit={() => setEditing('levels')}
                editPermission="users.assign-levels"
                description="Which records this account may read in the modules that classify them."
                empty={
                  userLevels.length === 0
                    ? {
                        title: 'No module classifies its records',
                        description:
                          'Nothing here carries an access level yet, so every reader sees every record their permissions allow.',
                      }
                    : undefined
                }
              >
                <PreviewTable
                  variant="table-display"
                  rows={userLevels.map((row) => ({
                    label: row.moduleName,
                    /*
                      The rung and its rank on one line: "Level 2" alone does not say which way
                      the ladder runs, and getting that backwards is the whole failure mode.

                      An untagged module renders an em dash through the component's own absent
                      -value rule rather than an invented word — the account has no level, which
                      is a different statement from any rung the ladder defines. What it MEANS
                      is said once, under the table, instead of on every row.
                    */
                    value:
                      row.accessLevelName != null
                        ? `${row.accessLevelName} — rank ${row.rank}`
                        : null,
                  }))}
                />
              </ProfileCard>

              {/*
                No Edit: an account's permissions are chosen once, when it is created, and
                re-seeded when its role changes. There is no endpoint to trim them after the
                fact, so an Edit button here would be a dead affordance.
              */}
              <ProfileCard
                padded
                id="user-profile-permissions"
                title="Effective permissions"
                count={user.permissionKeys.length}
                description={
                  user.hasPermissionOverrides
                    ? 'Chosen for this account when it was created. Changing its roles replaces this set.'
                    : 'Inherited from this account’s roles.'
                }
              >
                {catalogLoading ? (
                  <PanelSkeleton rows={5} />
                ) : (
                  <PermissionPicker
                    idPrefix="user-profile-perm"
                    catalog={catalog}
                    value={user.permissionKeys}
                    disabled
                    /*
                      A profile answers "what can this person do", so it opens on the modules
                      the account actually holds something in and offers the rest behind a
                      control. The role editor passes nothing here — hiding empty modules on a
                      surface for GRANTING would hide everything left to grant.
                    */
                    showOnlyEnabled
                    onChange={() => undefined}
                    emptyMessage="No permissions are registered yet."
                  />
                )}
              </ProfileCard>
            </>
          ) : null}

          {panel === 'credentials' ? (
            <ProfileCard
              id="user-profile-credentials"
              title="Password"
              onEdit={() => setEditing('password')}
              editPermission="users.reset-password"
              editLabel="Update"
              description="Resetting signs the account out everywhere and forces a change at next sign-in."
            >
              <PreviewTable variant="table-display" rows={credentialRows(user)} />
            </ProfileCard>
          ) : null}

          {panel === 'credentials' ? (
            <ProfileCard
              id="user-profile-pin"
              title="Sign-in PIN"
              onEdit={() => setEditing('pin-requirement')}
              editPermission="users.require-pin"
              editLabel={user.pinRequired ? 'Stop requiring' : 'Require'}
              menuItems={[
                {
                  key: 'user-profile-pin-set',
                  label: 'Set PIN',
                  icon: <KeyRound className="h-4 w-4" aria-hidden="true" />,
                  permission: 'users.reset-pin',
                  onClick: () => setEditing('pin'),
                },
              ]}
              description="A second factor asked for after the password. Setting it signs the account out everywhere and forces a change at next sign-in."
            >
              <PreviewTable variant="table-display" rows={pinRows(user)} />
            </ProfileCard>
          ) : null}

          {panel === 'sign-ins' ? (
            <ProfileCard
              padded
              id="user-profile-sign-ins"
              title="Sign-in activity"
              count={activity.total}
            >
              <DataTablePanel
                id="user-profile-sign-ins"
                state={activity}
                columns={SIGN_IN_COLUMNS}
                rowKey={(attempt) => String(attempt.id)}
                searchPlaceholder="Search by IP, email, or failure reason…"
                emptyMessage="No sign-in attempts recorded for this account."
                emptySearchMessage="No sign-in attempt matches that search."
              />
            </ProfileCard>
          ) : null}
        </ProfileShell>
      ) : null}

      {editing === 'levels' && user ? (
        <EditUserAccessLevelsModal
          open
          userId={user.id}
          displayName={user.displayName}
          current={userLevels}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadLevels();
          }}
        />
      ) : null}

      <EditUserInfoModal
        user={editing === 'info' ? user : null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />

      <PinRequirementDialog
        user={editing === 'pin-requirement' ? user : null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          reload();
        }}
      />

      <ResetUserPinModal
        user={editing === 'pin' ? user : null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          reload();
        }}
      />

      <ResetUserPasswordModal
        user={editing === 'password' ? user : null}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          reload();
        }}
      />

      <EditUserRoleModal
        user={editing === 'roles' ? user : null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          reload();
        }}
      />

      <UserConfirmDialog
        request={confirming && user ? { kind: confirming, user } : null}
        onClose={() => setConfirming(null)}
        onDone={() => {
          setConfirming(null);
          reload();
        }}
      />
    </div>
  );
}

/**
 * The record's state in one line, with the action that changes it on the right.
 *
 * Rendered for every state, not only the bad ones. Order matters: a deactivated account
 * cannot sign in at all, so that outranks a lockout it may also be carrying.
 */
function LifecycleBanner({
  user,
  onAct,
}: {
  user: UserDetail;
  onAct: (kind: UserConfirmKind) => void;
}) {
  if (!user.isActive) {
    return (
      <ProfileBanner
        id="user-profile-banner"
        tone="slate"
        icon={<Ban className="h-4 w-4" aria-hidden="true" />}
        message="This account is disabled and cannot sign in. The record is kept, and enabling it restores the access it had."
        action={
          // Gates the button, not the banner: a read-only viewer still needs to see the state.
          <PermissionGate permission="users.activate">
            <Button
              id="user-profile-enable"
              variant="secondary"
              leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
              onClick={() => onAct('activate')}
            >
              Enable
            </Button>
          </PermissionGate>
        }
      />
    );
  }

  return (
    <ProfileBanner
      id="user-profile-banner"
      tone="emerald"
      icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
      message="This account is active and can sign in."
      action={
        <PermissionGate permission="users.delete">
          <Button
            id="user-profile-disable"
            variant="danger"
            leading={<Ban className="h-4 w-4" aria-hidden="true" />}
            onClick={() => onAct('deactivate')}
          >
            Disable
          </Button>
        </PermissionGate>
      }
    />
  );
}

/** The editable identity of the account. */
function basicInfoRows(user: UserDetail): PreviewRow[] {
  return [
    { label: 'Alias', value: user.alias },
    { label: 'Email', value: <span className="font-mono">{user.email}</span> },
  ];
}

/** The credential's state. Its own panel, because resetting it is its own transaction. */
/**
 * What the PIN card states, and deliberately all it states.
 *
 * There is nothing here that could be turned back into the PIN: whether one exists, whether it
 * is demanded, and when it was last set. The value is never read by anything, including this.
 */
function pinRows(user: UserDetail): PreviewRow[] {
  return [
    {
      label: 'Required',
      value: user.pinRequired ? 'Asked for at every sign-in' : 'Not asked for',
    },
    {
      // "Required but not chosen" is a real state and the one worth naming: the next sign-in
      // asks the holder to pick one, and nobody has to do anything in the meantime. Backed by
      // MustChangePin for the issued case, as the password row is by MustChangePassword.
      label: 'Chosen',
      value: !user.hasPin
        ? user.pinRequired
          ? 'Not yet — they will be asked at their next sign-in'
          : 'Not set'
        : user.mustChangePin
          ? 'Set by an administrator — must be changed at next sign-in'
          : 'Set by the account holder',
    },
    { label: 'Last set', value: stamp(user.pinSetAt) },
  ];
}

function credentialRows(user: UserDetail): PreviewRow[] {
  return [
    {
      // Backed by MustChangePassword: true whenever an administrator issues or resets the
      // password, cleared only when the holder changes it themselves.
      label: 'Password',
      value: user.mustChangePassword
        ? 'Must be changed at next sign-in'
        : 'Changed by the account holder',
    },
    { label: 'Last changed', value: stamp(user.passwordChangedAt) },
    { label: 'Age', value: passwordAge(user.passwordChangedAt) },
  ];
}

/**
 * How long the current password has been in force. Stated in whole days because that is the
 * unit a rotation policy is written in; under a day it is "today", which is more useful than
 * a count of hours.
 */
function passwordAge(changedAt: string | null): string | null {
  if (!changedAt) {
    return null;
  }

  const days = Math.floor((Date.now() - new Date(changedAt).getTime()) / 86_400_000);

  if (days < 0) return null;
  if (days === 0) return 'Set today';

  return `${days} ${days === 1 ? 'day' : 'days'} old`;
}

/** Written by the system. Never editable — hence no Edit on the card that holds it. */
function auditRows(user: UserDetail): PreviewRow[] {
  return [
    { label: 'Account ID', value: <span className="font-mono tabular-nums">{user.id}</span> },
    { label: 'Last sign-in', value: stamp(user.lastLoginAt) ?? 'Never' },
    { label: 'Created by', value: user.createdByName },
    { label: 'Created at', value: stamp(user.createdAt) },
    { label: 'Updated by', value: user.updatedByName },
    { label: 'Updated at', value: stamp(user.updatedAt) },
  ];
}

/** A timestamp, or null so `<PreviewTable>` renders its em-dash. */
function stamp(value: string | null | undefined): ReactNode {
  return value ? (
    <time dateTime={toIso(value)} className="font-mono tabular-nums">
      {formatDateTime(value)}
    </time>
  ) : null;
}

/** The sign-in table's columns. Every one the server can order by is sortable. */
const SIGN_IN_COLUMNS: DataColumn<LoginAttempt>[] = [
  {
    key: 'occurredAt',
    header: 'When',
    width: 210,
    sortable: true,
    render: (attempt) => (
      <time
        dateTime={toIso(attempt.occurredAt)}
        className="font-mono tabular-nums text-sm text-slate-600"
      >
        {formatDateTime(attempt.occurredAt)}
      </time>
    ),
  },
  {
    key: 'succeeded',
    header: 'Result',
    width: 170,
    sortable: true,
    render: (attempt) => (
      <>
        <StatusText tone={attempt.succeeded ? 'active' : 'blocked'}>
          {attempt.succeeded ? 'success' : 'failed'}
        </StatusText>
        {attempt.failureReason ? (
          <span className="mt-0.5 block text-xs text-slate-500">{attempt.failureReason}</span>
        ) : null}
      </>
    ),
  },
  {
    key: 'clientIp',
    header: 'From',
    width: 150,
    sortable: true,
    render: (attempt) => (
      <span className="font-mono text-xs text-slate-500">{attempt.clientIp ?? '—'}</span>
    ),
  },
];

function PanelSkeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/** Mirrors the loaded layout so nothing shifts when the account lands. */
function ProfileSkeleton() {
  return (
    <div className="flex h-full min-h-0 min-w-0">
      <div className="flex w-72 shrink-0 flex-col border-r border-slate-200">
        <div className="flex flex-col items-center gap-2 border-b border-slate-200 px-4 py-6">
          <Skeleton className="h-24 w-24 rounded-full" />
          <Skeleton className="mt-1 h-4 w-32" />
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-3 w-24" />
        </div>

        <div className="flex flex-col gap-1 p-2">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-5 bg-slate-50/60 p-5">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-48 w-full rounded-xl" />
        ))}
      </div>
    </div>
  );
}
